/// <amd-module name="Widgets/PullRequestsWidget/components/PullRequestDataProvider" />
import * as Git from "Repos/Common/Contracts/Git";
import { getGitClient } from "Widgets/PullRequestsWidget/RestClient/Git"
import { IVssPageContext } from "VSS/Platform/Context";
import { IVssPageService } from "VSS/Platform/Page";
import { PullRequestWidgetConstants, SortCriteria, QueryType, PullRequestVoteStatus } from 'Widgets/Configs/PullrequestWidgetConfig/PullRequestManager'
import { PullRequestWidgetSettings } from "WidgetCommon/ConfigContracts/PullRequestsWidgetSettings";
import { IVssPerformanceService } from "VSS/Platform/Performance";

export class PullRequestResult {
    constructor(total: number, pullRequests: Git.GitPullRequest[], repositoryId: string, repositoryName: string, queryType: QueryType, teamId: string){
        this.Total = total;
        this.PullRequests = pullRequests;      
        this.RepositoryId = repositoryId;
        this.RepositoryName = repositoryName;  
        this.QueryType = queryType;
        this.TeamId = teamId
    }
    Total: number;
    PullRequests: Git.GitPullRequest[];
    RepositoryId: string;
    RepositoryName: string;
    QueryType: QueryType;
    TeamId: string;
}

export interface ICachedPullRequestData {
    [key: string]: PullRequestResult;
}

export class PullRequestDataProvider{
    private _pageContext: IVssPageContext;
    private _pullRequestResultsCache: ICachedPullRequestData;
    private _widgetSettings: PullRequestWidgetSettings;
    private performanceService : IVssPerformanceService;
    private _widgetId: string;

    constructor(pageContext: IVssPageContext, widgetSettings: string, widgetId: string) {
        this._pageContext = pageContext;
        this._widgetSettings = JSON.parse(widgetSettings);
        this._pullRequestResultsCache = {};
        this._widgetId = widgetId;
        this.performanceService = this._pageContext.getService<IVssPerformanceService>("IVssPerformanceService");
    }

    public async getResults(
        pullRequestsResultsCache: ICachedPullRequestData | undefined,
        updatePullRequestsResultsCache: (data: ICachedPullRequestData) => void): Promise<void> {
        
        this.performanceService.startScenario("pull-request-widget-get-pr-data", false);
        const promises: Promise<PullRequestResult>[] = [];
        let prCount: number = 0;
        
        this._widgetSettings.selectedRepos?.forEach(repo => {
            // fetch data if not available in cache
            if(pullRequestsResultsCache){

                // check if data is of 
                // (1) different query type (2) different team id
                if(!pullRequestsResultsCache[repo.id] 
                    || (
                        pullRequestsResultsCache[repo.id] 
                        && (
                            pullRequestsResultsCache[repo.id].QueryType !== this._widgetSettings.queryType
                            || pullRequestsResultsCache[repo.id].TeamId !== this._widgetSettings.teamId
                        )
                    )
                )
                {
                    promises.push(
                        this._getPullRequestsVersionControl(
                            repo.id, 
                            repo.name,
                            this._widgetSettings.queryType, 
                            this._widgetSettings.teamId, 
                            PullRequestWidgetConstants.DEFAULT_MAX_QUERY_RESULT_COUNT
                        )
                    );
                }
            }
        });

        Promise.all(promises).then((contents) => {
            prCount = contents.length;
            contents.forEach((repoData: PullRequestResult) => {
                this._pullRequestResultsCache[repoData.RepositoryId] = repoData;
            });
            
            updatePullRequestsResultsCache({...pullRequestsResultsCache, ...this._pullRequestResultsCache});
            this.performanceService.endScenario("Dashboards","pull-request-widget-get-pr-data", false, undefined, 
            { 
                "widgetId": this._widgetId,
                "widgetNme": "PullRequestWidgetNew",
                "totalRepoCount": this._widgetSettings.selectedRepos?.length,
                "searchCriteria": this._widgetSettings.queryType,
                "totalPRCount": prCount
            });
        });
    }

    private async _getPullRequestsVersionControl(repoId: string, repoName: string, queryType: QueryType, teamId: string, top?: number, sortCriteria?: SortCriteria): Promise<PullRequestResult> {
        top = top || PullRequestWidgetConstants.DEFAULT_MAX_QUERY_RESULT_COUNT;
        sortCriteria = sortCriteria || SortCriteria.CreatedDate;
        queryType = queryType || (QueryType.AssignedToMe | QueryType.CreatedByMe);

        let results: Git.GitPullRequest[];

        const vssPageService = this._pageContext.getService<IVssPageService>("IVssPageService");
        const vssPageData = vssPageService.getData();
        let userId = vssPageData ? vssPageData.user.id : undefined;
        
        switch (queryType) {
        case QueryType.AssignedToTeam:
            results = await this._beginGetPullRequestsVersionControl(repoId, this._getPullRequestSearchCriteria(undefined, teamId), top);
            break;

        case QueryType.AssignedToMe:
            results = await this._beginGetPullRequestsVersionControl(repoId, this._getPullRequestSearchCriteria(undefined, userId), top);
            break;

        case QueryType.CreatedByMe:
            results = await this._beginGetPullRequestsVersionControl(repoId, this._getPullRequestSearchCriteria(userId, undefined), top);
            break;

        default:
            throw new Error("Invalid queryType " + queryType);
        }

        const total: number = results.length;

        PullRequestDataProvider._sortPullRequests(results, sortCriteria);
        const filtered: Git.GitPullRequest[] = results.slice(0, top);

        return new PullRequestResult(Math.min(total, top), filtered, repoId, repoName, queryType, teamId);
    }

    private async _beginGetPullRequestsVersionControl(repoId: string, searchCriteria: Git.GitPullRequestSearchCriteria, top: number): Promise<Git.GitPullRequest[]> {
        this.performanceService.startScenario("pull-request-widget-get-pr-data-api", false);
        const _gitHttpClient = getGitClient(this._pageContext);
        var result = await _gitHttpClient.getPullRequests(repoId, searchCriteria, undefined, undefined, undefined, top + 1);
        this.performanceService.endScenario("Dashboards","pull-request-widget-get-pr-data-api", false, undefined, 
            { 
                "widgetId": this._widgetId,
                "widgetNme": "PullRequestWidgetNew",
                "repoId": repoId,
                "searchCriteria": searchCriteria,
                "prApiResults": result.length
            });
        return result;
    }

    public static getMinimumVoteStatus(pullRequest: Git.GitPullRequest): PullRequestVoteStatus {
        var minVote = PullRequestVoteStatus.APPROVE;
        var maxVote = PullRequestVoteStatus.REJECT;

        var reviewers = pullRequest.reviewers;
        if (reviewers == null)
            return PullRequestVoteStatus.NONE;

        for (var i = 0, len = reviewers.length; i < len; i++) {
            if (reviewers[i].vote < minVote) {
                minVote = reviewers[i].vote;
            }
            if (reviewers[i].vote > maxVote) {
                maxVote = reviewers[i].vote;
            }
        }

        if (minVote === PullRequestVoteStatus.NONE)
            return maxVote;

        return minVote;
    }

    private _getPullRequestSearchCriteria(creatorId?: string, reviewerId?: string): Git.GitPullRequestSearchCriteria {
        return {
            repositoryId: "",
            includeLinks: false,
            status: Git.PullRequestStatus.Active,
            creatorId: creatorId,
            reviewerId: reviewerId,
            sourceRefName: "",
            targetRefName: ""
        } as Git.GitPullRequestSearchCriteria;
    }

    private static _sortPullRequests(pullRequests: Git.GitPullRequest[], sortCriteria: SortCriteria | undefined) {
        if (sortCriteria !== undefined && sortCriteria === SortCriteria.CreatedDate) {
            pullRequests.sort((a, b) => b.creationDate.getTime() - a.creationDate.getTime());
            return;
        }
        throw new Error("Invalid sortCriteria");
    }
}
